'use client';

import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { Button } from '@/components/common/Button';
import { FormInput } from '@/components/common/form';

interface DeleteTenantDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  tenantName: string;
}

export const DeleteTenantDialog: React.FC<DeleteTenantDialogProps> = ({ open, onClose, onConfirm, tenantName }) => {
  const [confirmationText, setConfirmationText] = useState('');

  useEffect(() => {
    if (open) {
      setConfirmationText('');
    }
  }, [open]);

  const isConfirmed = confirmationText === 'DELETE';

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-lg w-full rounded-lg bg-white p-6">
          <Dialog.Title className="text-lg font-medium text-red-900">
            Delete Tenant: {tenantName}
          </Dialog.Title>

          <div className="mt-4 space-y-4">
            <p className="text-sm text-gray-600">
              This action is irreversible. All data associated with this tenant, including sites, articles, users, and billing information, will be permanently deleted.
            </p>
            <p className="text-sm font-semibold text-gray-800">
              To confirm, please type "DELETE" in the box below.
            </p>
            <div>
              <FormInput
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder='Type DELETE to confirm'
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button variant="destructive" onClick={onConfirm} disabled={!isConfirmed}>
              Delete Tenant
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
